<template>
  <main class="login-page">
    <div class="login-layout">
      <div class="login-brand">
        <span class="brand-mark">Q</span>
        <span>
          Quirco
          <span class="brand-secondary">Saas</span>
        </span>
      </div>
      <Card class="login-card">
        <CardHeader><CardTitle class="text-2xl">Войти в аккаунт</CardTitle></CardHeader>
        <CardContent>
          <form v-if="showPasswordLogin" class="space-y-5" @submit.prevent="login">
            <label class="ui-field">
              <span class="ui-label">Логин</span>
              <Input
                v-model="username"
                name="login"
                autocomplete="username"
                placeholder="Доменное имя"
                :disabled="loading"
                required
                autofocus
              />
            </label>
            <label class="ui-field">
              <span class="ui-label">Пароль</span>
              <Input
                id="password"
                v-model="password"
                name="password"
                autocomplete="current-password"
                type="password"
                placeholder="Пароль"
                :disabled="loading"
                required
              />
            </label>
            <Button type="submit" class="w-full" :disabled="!username || !password || loading">
              <Spinner v-if="loading" />
              Войти
              <ArrowRight v-if="!loading" :size="16" />
            </Button>
          </form>
          <p v-if="message" role="alert" class="ui-alert ui-alert--error mt-4">{{ message }}</p>
          <template v-if="showOidcLogin">
            <div v-if="showPasswordLogin" class="login-divider"><span>или</span></div>
            <Button variant="outline" class="w-full" :disabled="loading" @click="loginWithOidc">
              <ShieldCheck :size="17" />
              Войти через SSO
            </Button>
          </template>
        </CardContent>
      </Card>
    </div>
  </main>
</template>

<script setup lang="ts">
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { ArrowRight, ShieldCheck } from '@lucide/vue';

import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useMainStore } from '../store';
import { useRouter, useRoute } from 'vue-router';

const router = useRouter();
const route = useRoute();
const store = useMainStore();

const username = ref('');
const password = ref('');
const message = ref('');
const loading = ref(false);
const mounted = ref(false);
const authMethods = ref<string[]>(['password']);
const oidcLoginUrl = ref<string | null>(null);

const showPasswordLogin = computed(() => authMethods.value.includes('password'));
const showOidcLogin = computed(() => authMethods.value.includes('oidc') && oidcLoginUrl.value);

onMounted(async () => {
  requestAnimationFrame(() => {
    mounted.value = true;
  });

  if (route.query.error === 'oidc') {
    message.value = 'Ошибка входа через SSO';
  }

  try {
    const res = (await axios.get('/api/auth/methods/')).data;
    authMethods.value = res.methods;
    oidcLoginUrl.value = res.oidc_login_url;
  } catch {
    authMethods.value = ['password'];
  }
});

function loginWithOidc() {
  if (!oidcLoginUrl.value) return;

  const next = (route.query.next as string) || '/';
  const params = new URLSearchParams({ next });
  window.location.href = `${oidcLoginUrl.value}?${params.toString()}`;
}

async function login() {
  const data = {
    username: username.value,
    password: password.value,
  };
  loading.value = true;
  try {
    const res = (await axios.post('/api/auth/login/', data)).data;
    store.login({ access: res.access, refresh: res.refresh });
    await store.setCurrentUserName(data.username);

    const next = route.query['next'] as string;
    if (next != null) {
      await router.replace(next);
    } else {
      await router.replace('/');
    }
  } catch (error) {
    message.value = 'Ошибка входа';
  } finally {
    loading.value = false;
  }
}
</script>
